import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Stack,
  Tooltip,
} from '@mui/material';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AssignmentIndOutlinedIcon from '@mui/icons-material/AssignmentIndOutlined';
import { PlanRole, Participant, PlanStep, StepStatus } from '../types/generated/graphql';
import { colors } from '../theme/colors';
import AssigneeControl from './shared/AssigneeControl';
import AssignOrRequestSheet from './shared/AssignOrRequestSheet';

interface ParticipantsPanelProps {
  /** Plan ID for mutations */
  planId: string;
  /** Plan roles - the primary way work is organized */
  roles: PlanRole[];
  /** Participants in the plan */
  participants: Participant[];
  /** Steps for counting assignments */
  steps?: PlanStep[];
  /** Current user's participant ID (to show "You" label) */
  currentParticipantId?: string | null;
  /** Callback when clicking on a role/participant */
  onRoleClick?: (roleId: string) => void;
}

/**
 * Role-centric Team Panel
 *
 * Shows roles and who is assigned to each role, rather than listing
 * participants directly. This reflects the role-based assignment model
 * where work is assigned to roles, not individuals.
 *
 * Uses the unified AssigneeControl and AssignmentSideSheet for assignment.
 */
export const ParticipantsPanel: React.FC<ParticipantsPanelProps> = ({
  planId,
  roles,
  participants,
  steps = [],
  currentParticipantId,
  onRoleClick,
}) => {
  // Side sheet state
  const [sideSheetOpen, setSideSheetOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<PlanRole | null>(null);

  // Count steps per role
  const stepCountByRole = useMemo(() => {
    const counts = new Map<string, { total: number; active: number }>();

    for (const role of roles) {
      counts.set(role.roleId, { total: 0, active: 0 });
    }

    for (const step of steps) {
      if (step.assignedRoleId && counts.has(step.assignedRoleId)) {
        const current = counts.get(step.assignedRoleId)!;
        current.total += 1;
        if (step.status !== StepStatus.Completed && step.status !== StepStatus.Skipped) {
          current.active += 1;
        }
      }
    }

    return counts;
  }, [roles, steps]);

  // Get participant by ID — checks both embedded participants and global person ID
  const getParticipantById = (id: string | null | undefined): Participant | null => {
    if (!id) return null;
    return participants.find((p) => p.id === id) || null;
  };

  // Build assignee display info from either the embedded participant or computed fields
  const getAssigneeForRole = (role: PlanRole): { participant: Participant | null; name: string | null; isYou: boolean } => {
    const participant = getParticipantById(role.defaultAssigneePersonId);
    if (participant) {
      return { participant, name: participant.name, isYou: isCurrentUser(participant.id) };
    }
    // Fall back to computed fields from PlanRole resolver
    const assigneeName = (role as any).assigneeName;
    const assigneeEmail = (role as any).assigneeEmail;
    if (assigneeName || assigneeEmail) {
      // Create a synthetic participant for display
      return {
        participant: { id: role.defaultAssigneePersonId || '', name: assigneeName || assigneeEmail || '', email: assigneeEmail } as Participant,
        name: assigneeName || assigneeEmail,
        isYou: false,
      };
    }
    return { participant: null, name: null, isYou: false };
  };

  // Check if participant is current user
  const isCurrentUser = (participantId: string | null | undefined): boolean => {
    return !!participantId && participantId === currentParticipantId;
  };

  // Handle opening the assignment side sheet
  const handleAssignRole = (role: PlanRole) => {
    setSelectedRole(role);
    setSideSheetOpen(true);
  };

  // Handle closing the side sheet
  const handleCloseSideSheet = () => {
    setSideSheetOpen(false);
    setSelectedRole(null);
  };

  // Empty state
  if (!roles || roles.length === 0) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          border: `1px solid ${colors.gray[200]}`,
          borderRadius: 2,
          textAlign: 'center',
        }}
      >
        <GroupsOutlinedIcon sx={{ fontSize: 40, color: colors.gray[300], mb: 1 }} />
        <Typography variant="body2" sx={{ color: colors.textSecondary }}>
          No roles defined yet
        </Typography>
      </Paper>
    );
  }

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${colors.gray[200]}`,
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 2,
            py: 1.5,
            bgcolor: colors.gray[50],
            borderBottom: `1px solid ${colors.gray[200]}`,
          }}
        >
          <GroupsOutlinedIcon sx={{ fontSize: 18, color: colors.gray[500] }} />
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 600, color: colors.textPrimary, flex: 1 }}
          >
            Roles
          </Typography>
          <Chip
            label={roles.length}
            size="small"
            sx={{
              bgcolor: 'white',
              height: 22,
              '& .MuiChip-label': {
                px: 1,
                fontSize: '0.75rem',
                color: colors.textSecondary,
              },
            }}
          />
        </Box>

        {/* Role list */}
        <Stack divider={<Box sx={{ borderBottom: `1px solid ${colors.gray[100]}` }} />}>
          {roles.map((role) => {
            const { participant: assignee, isYou } = getAssigneeForRole(role);
            const isAssigned = !!assignee;
            const stepCounts = stepCountByRole.get(role.roleId) || { total: 0, active: 0 };

            return (
              <Box
                key={role.roleId}
                onClick={() => onRoleClick?.(role.roleId)}
                sx={{
                  px: 2,
                  py: 2,
                  cursor: onRoleClick ? 'pointer' : 'default',
                  transition: 'background-color 0.15s',
                  '&:hover': onRoleClick ? { bgcolor: colors.gray[50] } : undefined,
                }}
              >
                {/* Role header row */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  {/* Status icon */}
                  {isAssigned ? (
                    <CheckCircleIcon sx={{ fontSize: 16, color: colors.green[600] }} />
                  ) : (
                    <WarningAmberIcon
                      sx={{ fontSize: 16, color: role.required ? colors.amber[600] : colors.gray[400] }}
                    />
                  )}

                  {/* Role name */}
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: colors.textPrimary,
                      flex: 1,
                    }}
                  >
                    {role.name}
                  </Typography>

                  {/* Required badge */}
                  {role.required && (
                    <Chip
                      label="Required"
                      size="small"
                      sx={{
                        height: 18,
                        bgcolor: isAssigned ? colors.green[50] : colors.amber[50],
                        color: isAssigned ? colors.green[700] : colors.amber[700],
                        fontSize: '0.6rem',
                        fontWeight: 600,
                        '& .MuiChip-label': { px: 0.75 },
                      }}
                    />
                  )}
                </Box>

                {/* Assignee control - unified component */}
                <Box sx={{ ml: 3 }}>
                  <AssigneeControl
                    assignee={assignee}
                    isCurrentUser={isYou}
                    emptyLabel="Assign someone"
                    mode="role"
                    onAssign={() => handleAssignRole(role)}
                    compact={false}
                  />
                </Box>

                {/* Task count */}
                {stepCounts.total > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 3, mt: 1.5 }}>
                    <Tooltip title={`${stepCounts.active} active, ${stepCounts.total - stepCounts.active} completed`}>
                      <Chip
                        icon={<AssignmentIndOutlinedIcon sx={{ fontSize: 12 }} />}
                        label={`${stepCounts.active} task${stepCounts.active !== 1 ? 's' : ''}`}
                        size="small"
                        sx={{
                          height: 20,
                          bgcolor: isAssigned ? colors.gray[100] : colors.amber[50],
                          color: isAssigned ? colors.textSecondary : colors.amber[700],
                          fontSize: '0.7rem',
                          '& .MuiChip-icon': {
                            color: isAssigned ? colors.gray[400] : colors.amber[600],
                          },
                          '& .MuiChip-label': { px: 0.5 },
                        }}
                      />
                    </Tooltip>
                    {!isAssigned && stepCounts.active > 0 && (
                      <Typography
                        variant="caption"
                        sx={{ color: colors.amber[700], fontWeight: 500 }}
                      >
                        waiting
                      </Typography>
                    )}
                  </Box>
                )}

                {/* Role description */}
                {role.description && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: colors.textSecondary,
                      display: 'block',
                      ml: 3,
                      mt: 0.5,
                      fontStyle: 'italic',
                    }}
                  >
                    {role.description}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Stack>
      </Paper>

      {/* Assignment Side Sheet */}
      <AssignOrRequestSheet
        open={sideSheetOpen}
        onClose={handleCloseSideSheet}
        planId={planId}
        mode="role"
        role={selectedRole}
        participants={participants}
        roles={roles}
        onComplete={handleCloseSideSheet}
      />
    </>
  );
};

export default ParticipantsPanel;
